import { Helmet } from 'react-helmet-async';

interface Address {
  streetAddress?: string;
  addressLocality?: string; // City
  addressRegion?: string; // State
  postalCode?: string;
  addressCountry?: string;
}

interface GeoCoordinates {
  latitude: number;
  longitude: number;
}

interface OpeningHours {
  dayOfWeek: string | string[];
  opens: string; // HH:MM format
  closes: string; // HH:MM format
}

interface OrganizationSchemaProps {
  name: string;
  description?: string;
  url?: string;
  logo?: string;
  image?: string;
  telephone?: string;
  email?: string;
  address?: Address;
  geo?: GeoCoordinates;
  priceRange?: string; // e.g., "$$" or "$100-$500"
  openingHours?: OpeningHours[];
  sameAs?: string[]; // Social media URLs
  areaServed?: string | string[];
  paymentAccepted?: string[];
  currenciesAccepted?: string;
  aggregateRating?: {
    ratingValue: number;
    reviewCount: number;
    bestRating?: number;
    worstRating?: number;
  };
}

/**
 * LocalBusiness/Organization structured data component following schema.org specification.
 * Generates comprehensive JSON-LD markup for local business information.
 *
 * @see https://schema.org/LocalBusiness
 * @see https://developers.google.com/search/docs/appearance/structured-data/local-business
 */
export const OrganizationSchema = ({
  name,
  description,
  url,
  logo,
  image,
  telephone,
  email,
  address,
  geo,
  priceRange,
  openingHours,
  sameAs,
  areaServed,
  paymentAccepted,
  currenciesAccepted,
  aggregateRating,
}: OrganizationSchemaProps) => {
  if (!name) {
    return null;
  }

  const organizationSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'WasteManagementCompany'],
    name,
    ...(description && { description }),
    ...(url && { url }),
    ...(logo && { logo }),
    ...(image && { image }),
    ...(telephone && { telephone }),
    ...(email && { email }),
    ...(priceRange && { priceRange }),
    ...(currenciesAccepted && { currenciesAccepted }),
  };

  // Add address if provided
  if (address && (address.streetAddress || address.addressLocality)) {
    organizationSchema.address = {
      '@type': 'PostalAddress',
      ...(address.streetAddress && { streetAddress: address.streetAddress }),
      ...(address.addressLocality && {
        addressLocality: address.addressLocality,
      }),
      ...(address.addressRegion && { addressRegion: address.addressRegion }),
      ...(address.postalCode && { postalCode: address.postalCode }),
      ...(address.addressCountry && {
        addressCountry: address.addressCountry || 'US',
      }),
    };
  }

  // Add geo coordinates if provided
  if (geo) {
    organizationSchema.geo = {
      '@type': 'GeoCoordinates',
      latitude: geo.latitude,
      longitude: geo.longitude,
    };
  }

  // Add opening hours if provided
  if (openingHours && openingHours.length > 0) {
    organizationSchema.openingHoursSpecification = openingHours.map((hours) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: hours.dayOfWeek,
      opens: hours.opens,
      closes: hours.closes,
    }));
  }

  // Add social media links if provided
  if (sameAs && sameAs.length > 0) {
    organizationSchema.sameAs = sameAs.filter(Boolean);
  }

  // Add area served if provided
  if (areaServed) {
    organizationSchema.areaServed = Array.isArray(areaServed)
      ? areaServed.map((area) => ({
          '@type': 'State',
          name: area,
        }))
      : {
          '@type': 'State',
          name: areaServed,
        };
  }

  // Add payment methods if provided
  if (paymentAccepted && paymentAccepted.length > 0) {
    organizationSchema.paymentAccepted = paymentAccepted.join(', ');
  }

  // Add aggregate rating if provided
  if (aggregateRating) {
    organizationSchema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: aggregateRating.ratingValue,
      reviewCount: aggregateRating.reviewCount,
      bestRating: aggregateRating.bestRating || 5,
      worstRating: aggregateRating.worstRating || 1,
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
    </Helmet>
  );
};

export default OrganizationSchema;
